/**
 * Admin Dashboard
 *
 * High-level overview pulled from existing operational endpoints:
 *   - getSystemInsights      → totals, search index doc count, partners,
 *                              upcoming, recent activity
 *   - diagnoseSyncStatus     → DB ↔ search index sync health summary
 *   - getQueueStats (opt.)   → queue depth and Function App state.
 *                              The backend may not expose this yet; we
 *                              show a graceful "not available" state.
 *
 * Each card links to the relevant operational page for drill-down.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  Database,
  Search,
  Users,
  CalendarClock,
  Stethoscope,
  ListChecks,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Server,
  Copy,
  Workflow,
  Boxes,
  BarChart2,
  Loader2,
  BrainCircuit,
  Layers3,
} from 'lucide-react';
import styled, { keyframes } from 'styled-components';
import { AdminLayout } from '../components/AdminLayout';
import {
  getAdminSystemInsights,
  diagnoseSyncStatus,
  getQueueStats,
  getActiveRuns,
  type DiagnoseSyncResponse,
  type QueueStatsResponse,
  type ActiveRun,
} from '../services/adminApi';
import {
  Card,
  CardTitle,
  CardDescription,
  StatGrid,
  Metric,
  Banner,
  PrimaryButton,
  SecondaryButton,
  ButtonRow,
  SampleTable,
} from '../components/admin/AdminUI';
import { colors, spacing, typography, borderRadius, shadows } from '../styles/theme';

// ---------------------------------------------------------------------------
// Hero / welcome card
// ---------------------------------------------------------------------------

const WelcomeCard = styled.div`
  background: ${colors.gradients.primary};
  color: ${colors.white};
  border-radius: ${borderRadius.xl};
  padding: ${spacing[6]} ${spacing[8]};
  margin-bottom: ${spacing[6]};
  box-shadow: ${shadows.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing[4]};
  flex-wrap: wrap;
`;

const WelcomeText = styled.div`
  h2 { font-size: ${typography.fontSizes['2xl']}; font-weight: ${typography.fontWeights.bold}; margin: 0 0 ${spacing[1]} 0; }
  p  { margin: 0; font-size: ${typography.fontSizes.sm}; opacity: 0.9; }
`;

// ---------------------------------------------------------------------------
// Live run pulsing badge
// ---------------------------------------------------------------------------

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
`;

const LiveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  padding: 2px ${spacing[2]};
  background: ${colors.success[100]};
  color: ${colors.success[700]};
  border: 1px solid ${colors.success[300]};
  border-radius: ${borderRadius.full};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.semibold};
  animation: ${pulse} 2s ease-in-out infinite;
`;

const StagePill = styled.span`
  display: inline-block;
  padding: 2px ${spacing[2]};
  background: ${colors.primary[50]};
  color: ${colors.primary[700]};
  border-radius: ${borderRadius.full};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.medium};
`;

// ---------------------------------------------------------------------------
// Quick-action tile grid
// ---------------------------------------------------------------------------

const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${spacing[4]};
  margin-bottom: ${spacing[6]};
`;

const Tile = styled.button`
  text-align: left;
  background: ${colors.white};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.lg};
  padding: ${spacing[5]};
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  font: inherit;

  &:hover {
    border-color: ${colors.primary[400]};
    box-shadow: ${shadows.md};
    transform: translateY(-2px);
  }
`;

const TileIcon = styled.div`
  width: 40px;
  height: 40px;
  background: ${colors.primary[50]};
  color: ${colors.primary[600]};
  border-radius: ${borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${spacing[2]};
`;

const TileTitle = styled.div`
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.primary};
  font-size: ${typography.fontSizes.base};
`;

const TileDescription = styled.div`
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.secondary};
  line-height: ${typography.lineHeights.relaxed};
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmt(v: unknown): string {
  return typeof v === 'number' ? v.toLocaleString() : '—';
}

function relativeTime(iso?: string): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const delta = Date.now() - t;
  if (delta < 60_000)    return 'just now';
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}m ago`;
  if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)}h ago`;
  return new Date(iso).toLocaleString();
}

function elapsedSince(iso?: string): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}m ${rem}s`;
}

const STAGE_LABELS: Record<string, string> = {
  init:                 'Initializing',
  validation:           'Validating source URL',
  screenshot_capture:   'Capturing screenshot…',
  markdown_extracted:   'Markdown extracted',
  screenshot_extracted: 'Screenshot extracted',
  processing_events:    'Processing events…',
  processEventsActivity:'Processing events…',
  captureWebsiteActivity: 'Screenshot failed',
  extractEventsActivity:  'Extraction failed',
  markdown_extraction:  'No events in markdown',
  screenshot_extraction:'No events in screenshot',
};

function stageLabel(stage?: string | null): string {
  if (!stage) return 'Running';
  return STAGE_LABELS[stage] ?? stage;
}

// ---------------------------------------------------------------------------
// SessionStorage cache — stale-while-revalidate. Survives nav within the SPA
// session so revisiting /admin renders instantly while a fresh fetch happens
// in the background.
// ---------------------------------------------------------------------------

const CACHE_PREFIX = 'admin:dashboard:';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function readCache<T>(key: string): { value: T; cachedAt: number } | null {
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { value: T; cachedAt: number };
    if (Date.now() - parsed.cachedAt > CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache<T>(key: string, value: T): void {
  try {
    sessionStorage.setItem(
      CACHE_PREFIX + key,
      JSON.stringify({ value, cachedAt: Date.now() }),
    );
  } catch {
    // ignore quota errors
  }
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const AdminDashboardPage: React.FC = () => {
  const navigate = useNavigate();

  const [insights, setInsights] = useState<any>(() => readCache<any>('insights')?.value ?? null);
  const [insightsErr, setInsightsErr] = useState<string | null>(null);

  const [sync, setSync] = useState<DiagnoseSyncResponse | null>(null);
  const [syncErr, setSyncErr] = useState<string | null>(null);
  const [syncLoading, setSyncLoading] = useState(false);

  const [queue, setQueue] = useState<QueueStatsResponse | null>(
    () => readCache<QueueStatsResponse>('queue')?.value ?? null,
  );
  const [queueErr, setQueueErr] = useState<string | null>(null);
  /** True if the backend explicitly does not expose getQueueStats (404). */
  const [queueUnavailable, setQueueUnavailable] = useState(false);

  const [activeRuns, setActiveRuns] = useState<ActiveRun[]>([]);
  const [activeRunsErr, setActiveRunsErr] = useState<string | null>(null);
  const [activeRunsLoading, setActiveRunsLoading] = useState(false);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(() => {
    const c = readCache<any>('insights');
    return c ? new Date(c.cachedAt) : null;
  });

  const fetchActiveRuns = useCallback(async () => {
    try {
      const res = await getActiveRuns();
      setActiveRuns(res.runs ?? []);
      setActiveRunsErr(null);
    } catch (e: any) {
      setActiveRunsErr(e?.message ?? 'Failed to load active runs');
    }
  }, []);

  // Poll active runs every 20 s while any are in progress.
  useEffect(() => {
    setActiveRunsLoading(true);
    fetchActiveRuns().finally(() => setActiveRunsLoading(false));
    pollTimerRef.current = setInterval(fetchActiveRuns, 20_000);
    return () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); };
  }, [fetchActiveRuns]);

  /**
   * Initial-load fan-out. Fetches the cheap, high-value endpoints in parallel.
   * `diagnoseSyncStatus` is intentionally excluded — it's an expensive scan
   * and has its own dedicated button below. It will only run on user action.
   */
  const loadAll = useCallback(async () => {
    setRefreshing(true);
    setInsightsErr(null);
    setQueueErr(null);

    const [insightsRes, queueRes] = await Promise.allSettled([
      getAdminSystemInsights({ timeframe: 30 }),
      getQueueStats(),
    ]);

    if (insightsRes.status === 'fulfilled') {
      setInsights(insightsRes.value);
      writeCache('insights', insightsRes.value);
    } else {
      setInsightsErr((insightsRes.reason as Error)?.message ?? 'Failed to load insights');
    }

    if (queueRes.status === 'fulfilled') {
      if (queueRes.value === null) {
        setQueueUnavailable(true);
        setQueue(null);
      } else {
        setQueueUnavailable(false);
        setQueue(queueRes.value);
        writeCache('queue', queueRes.value);
      }
    } else {
      setQueueErr((queueRes.reason as Error)?.message ?? 'Failed to load queue stats');
    }

    setLastRefreshed(new Date());
    setRefreshing(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Sync block uses its own button to be explicit since it's an expensive scan.
  const refreshSync = async () => {
    setSyncLoading(true);
    setSyncErr(null);
    try {
      const r = await diagnoseSyncStatus();
      setSync(r);
    } catch (e: any) {
      setSyncErr(e?.message ?? 'Failed');
    } finally {
      setSyncLoading(false);
    }
  };

  // Pull values out of the loose insights shape.
  const stats = insights?.systemStats ?? {};
  const ins = insights?.insights ?? {};
  const totalEvents = stats?.eventInfo?.total ?? stats?.db?.eventInfo?.total;
  const searchDocs = stats?.searchIndex?.documentCount ?? stats?.search?.documentCount;
  const activePartners = ins?.partners?.active;
  const upcoming = ins?.events?.upcoming;
  const recent = ins?.activity?.recentAdditions ?? {};

  // Aggregate queue depth across all queues for the headline card.
  const totalQueueDepth = queue?.queues?.reduce(
    (sum, q) => sum + (q.approximateMessageCount ?? 0),
    0,
  ) ?? 0;
  const totalPoison = queue?.queues?.reduce(
    (sum, q) => sum + (q.poisonMessageCount ?? 0),
    0,
  ) ?? 0;

  return (
    <AdminLayout
      currentPage="dashboard"
      pageTitle="Admin Dashboard"
      pageDescription="Operational overview — content, search index health, and processing queues."
    >
      <WelcomeCard>
        <WelcomeText>
          <h2>Welcome to Event Finder Admin</h2>
          <p>
            High-level health and content metrics. Use the quick actions below to
            drill into operations.
          </p>
        </WelcomeText>
        <ButtonRow>
          <SecondaryButton
            onClick={loadAll}
            disabled={refreshing}
            style={{ background: 'rgba(255,255,255,0.15)', color: 'white', borderColor: 'rgba(255,255,255,0.4)' }}
          >
            <RefreshCw size={16} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </SecondaryButton>
        </ButtonRow>
      </WelcomeCard>

      {lastRefreshed && (
        <div style={{ fontSize: 12, color: colors.text.tertiary, marginBottom: spacing[4] }}>
          Last refreshed {lastRefreshed.toLocaleTimeString()}
        </div>
      )}

      {/* ----------------------------- Top metrics ------------------------- */}
      {insightsErr ? (
        <Banner tone="error">Insights: {insightsErr}</Banner>
      ) : (
        <StatGrid>
          <Metric label="Total events"    value={fmt(totalEvents)} />
          <Metric label="Search docs"     value={fmt(searchDocs)} />
          <Metric label="Active tech companies" value={fmt(activePartners)} />
          <Metric label="Upcoming (30d)"  value={fmt(upcoming)} />
        </StatGrid>
      )}

      {/* ----------------------------- Recent activity --------------------- */}
      <Card>
        <CardTitle>Recent activity</CardTitle>
        <CardDescription>New events added in the last day, week, and month.</CardDescription>
        <StatGrid>
          <Metric label="Last 24h" value={fmt(recent.last24Hours ?? recent.last1Day)} />
          <Metric label="Last 7d"  value={fmt(recent.last7Days)} />
          <Metric label="Last 30d" value={fmt(recent.last30Days)} />
        </StatGrid>
      </Card>

      {/* ----------------------------- Active / live runs ------------------- */}
      <Card>
        <CardTitle style={{ display: 'flex', alignItems: 'center', gap: spacing[2] }}>
          <Loader2 size={18} style={{ verticalAlign: 'middle' }} />
          Live processing runs
          {activeRuns.length > 0 && (
            <LiveBadge>
              ● {activeRuns.length} active
            </LiveBadge>
          )}
        </CardTitle>
        <CardDescription>
          Runs currently in progress — auto-refreshes every 20 seconds.
        </CardDescription>

        {activeRunsErr && <Banner tone="error">{activeRunsErr}</Banner>}

        {!activeRunsLoading && activeRuns.length === 0 && !activeRunsErr && (
          <Banner tone="info">No runs in progress right now.</Banner>
        )}

        {activeRuns.length > 0 && (
          <SampleTable>
            <thead>
              <tr>
                <th>Entity</th>
                <th>Source</th>
                <th>Stage</th>
                <th style={{ textAlign: 'right' }}>Elapsed</th>
                <th style={{ textAlign: 'right' }}>Events found</th>
                <th style={{ textAlign: 'right' }}>New</th>
                <th style={{ textAlign: 'right' }}>Tokens</th>
              </tr>
            </thead>
            <tbody>
              {activeRuns.map(run => (
                <tr key={run.id}>
                  <td>{run.partnerName ?? run.entityName ?? `Source #${run.eventSourceId}`}</td>
                  <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <a href={run.source ?? '#'} target="_blank" rel="noopener noreferrer">
                      {run.source ? new URL(run.source).hostname : '—'}
                    </a>
                  </td>
                  <td><StagePill>{stageLabel(run.stage)}</StagePill></td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {elapsedSince(run.startTime)}
                  </td>
                  <td style={{ textAlign: 'right' }}>{run.totalEventsFound ?? '—'}</td>
                  <td style={{ textAlign: 'right' }}>{run.totalNewEvents ?? '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    {run.totalTokens != null ? run.totalTokens.toLocaleString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </SampleTable>
        )}

        <ButtonRow>
          <SecondaryButton onClick={fetchActiveRuns} disabled={activeRunsLoading}>
            <RefreshCw size={16} />
            Refresh now
          </SecondaryButton>
          <SecondaryButton onClick={() => navigate('/admin/processing-runs')}>
            <BarChart2 size={16} />
            View run history
          </SecondaryButton>
        </ButtonRow>
      </Card>

      {/* ----------------------------- Queue depth ------------------------- */}
      <Card>
        <CardTitle>
          <Server size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Processing queues
        </CardTitle>

        {queueErr && <Banner tone="error">{queueErr}</Banner>}

        {queueUnavailable && !queue && !queueErr && (
          <Banner tone="info">
            <strong>Queue stats endpoint not available.</strong> The backend
            does not expose <code>/api/getQueueStats</code> yet. Once added, this
            card will show queue depth, poison-message counts, and Function App
            state automatically.
          </Banner>
        )}

        {queue && (
          <>
            <StatGrid>
              <Metric
                label="Total queue depth"
                value={totalQueueDepth.toLocaleString()}
                tone={totalQueueDepth > 1000 ? 'warning' : totalQueueDepth > 0 ? 'neutral' : 'success'}
              />
              <Metric
                label="Poison messages"
                value={totalPoison.toLocaleString()}
                tone={totalPoison > 0 ? 'error' : 'success'}
              />
              {queue.functionApp?.state && (
                <Metric
                  label="Function App"
                  value={queue.functionApp.state}
                  tone={queue.functionApp.state.toLowerCase() === 'running' ? 'success' : 'warning'}
                  hint={queue.functionApp.region}
                />
              )}
              {typeof queue.functionApp?.instanceCount === 'number' && (
                <Metric label="Instances" value={queue.functionApp.instanceCount} />
              )}
            </StatGrid>

            {queue.queues && queue.queues.length > 0 && (
              <SampleTable>
                <thead>
                  <tr>
                    <th>Queue</th>
                    <th style={{ textAlign: 'right' }}>Depth</th>
                    <th style={{ textAlign: 'right' }}>Poison</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {queue.queues.map((q) => (
                    <tr key={q.name}>
                      <td><code>{q.name}</code></td>
                      <td style={{ textAlign: 'right' }}>{q.approximateMessageCount.toLocaleString()}</td>
                      <td style={{
                        textAlign: 'right',
                        color: (q.poisonMessageCount ?? 0) > 0 ? colors.error[600] : undefined,
                      }}>
                        {(q.poisonMessageCount ?? 0).toLocaleString()}
                      </td>
                      <td>{relativeTime(q.lastUpdated)}</td>
                    </tr>
                  ))}
                </tbody>
              </SampleTable>
            )}
          </>
        )}
      </Card>

      {/* ----------------------------- Sync health ------------------------- */}
      <Card>
        <CardTitle>
          <Stethoscope size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Search index health
        </CardTitle>

        {syncErr && <Banner tone="error">{syncErr}</Banner>}

        {!sync && !syncErr && !syncLoading && (
          <Banner tone="info">
            Sync diagnostics are not run on dashboard load (it's an expensive
            scan). Click <strong>Re-check sync</strong> below to run it now.
          </Banner>
        )}

        {sync && (
          <>
            <Banner tone={sync.overview.inSync ? 'success' : 'warning'}>
              {sync.overview.inSync ? (
                <>
                  <CheckCircle2 size={16} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />
                  Database and search index are in sync.
                </>
              ) : (
                <>
                  <AlertTriangle size={16} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />
                  Out of sync — {sync.missingFromSearch.count} missing,{' '}
                  {sync.orphanedInSearch.count} orphaned.
                </>
              )}
            </Banner>

            <StatGrid>
              <Metric label="DB events"   value={sync.overview.databaseEventCount.toLocaleString()} />
              <Metric label="Search docs" value={sync.overview.searchDocumentCount.toLocaleString()} />
              <Metric
                label="Δ"
                value={sync.overview.difference.toLocaleString()}
                tone={sync.overview.difference === 0 ? 'success' : 'warning'}
              />
              <Metric
                label="Missing"
                value={sync.missingFromSearch.count.toLocaleString()}
                tone={sync.missingFromSearch.count > 0 ? 'warning' : 'success'}
              />
              <Metric
                label="Orphaned"
                value={sync.orphanedInSearch.count.toLocaleString()}
                tone={sync.orphanedInSearch.count > 0 ? 'warning' : 'success'}
              />
            </StatGrid>
          </>
        )}

        <ButtonRow>
          <PrimaryButton onClick={refreshSync} disabled={syncLoading}>
            <RefreshCw size={16} />
            {syncLoading ? 'Checking…' : 'Re-check sync'}
          </PrimaryButton>
          <SecondaryButton onClick={() => navigate('/admin/search-health')}>
            View full health check
          </SecondaryButton>
        </ButtonRow>
      </Card>

      {/* ----------------------------- Quick actions ----------------------- */}
      <CardTitle style={{ marginTop: spacing[6] }}>Quick actions</CardTitle>
      <TileGrid>
        <Tile onClick={() => navigate('/admin/system-insights')}>
          <TileIcon><Activity size={20} /></TileIcon>
          <TileTitle>System Insights</TileTitle>
          <TileDescription>Charts and trends across content, entities, and locations.</TileDescription>
        </Tile>
        <Tile onClick={() => navigate('/admin/search-health')}>
          <TileIcon><Search size={20} /></TileIcon>
          <TileTitle>Search Index Health</TileTitle>
          <TileDescription>Compare the database against the Azure AI Search index.</TileDescription>
        </Tile>
        <Tile onClick={() => navigate('/admin/sync-index')}>
          <TileIcon><Database size={20} /></TileIcon>
          <TileTitle>Synchronize Index</TileTitle>
          <TileDescription>Add missing events and remove orphaned search documents.</TileDescription>
        </Tile>
        <Tile onClick={() => navigate('/admin/cleanup-orphans')}>
          <TileIcon><ListChecks size={20} /></TileIcon>
          <TileTitle>Remove Orphans</TileTitle>
          <TileDescription>Delete search documents whose database row is gone.</TileDescription>
        </Tile>
        <Tile onClick={() => navigate('/admin/duplicates')}>
          <TileIcon><Copy size={20} /></TileIcon>
          <TileTitle>Duplicate Events</TileTitle>
          <TileDescription>Find and remove duplicate EventInfo records, plus drop stale events.</TileDescription>
        </Tile>
        <Tile onClick={() => navigate('/admin/processing-runs')}>
          <TileIcon><BarChart2 size={20} /></TileIcon>
          <TileTitle>Processing Runs</TileTitle>
          <TileDescription>History of AI extraction runs with stats, token counts, and cost estimates.</TileDescription>
        </Tile>
        <Tile onClick={() => navigate('/admin/batches')}>
          <TileIcon><Layers3 size={20} /></TileIcon>
          <TileTitle>Extraction AI Batches</TileTitle>
          <TileDescription>Inspect Batch timing, payloads, token costs, fallbacks, and durable workflow errors.</TileDescription>
        </Tile>
        <Tile onClick={() => navigate('/admin/ai-decisions')}>
          <TileIcon><BrainCircuit size={20} /></TileIcon>
          <TileTitle>AI Decisions</TileTitle>
          <TileDescription>Audit relevance, relationships, classifications, logos, evidence, and overrides.</TileDescription>
        </Tile>
        <Tile onClick={() => navigate('/admin/workflow-metadata')}>
          <TileIcon><Workflow size={20} /></TileIcon>
          <TileTitle>Workflow Runs</TileTitle>
          <TileDescription>Audit WorkflowMetadata, find orphan entities, cascade-delete failed sources.</TileDescription>
        </Tile>
        <Tile onClick={() => navigate('/admin/how-it-works')}>
          <TileIcon><CalendarClock size={20} /></TileIcon>
          <TileTitle>How It Works</TileTitle>
          <TileDescription>Plain-language guide to the two jobs that keep the site full of events.</TileDescription>
        </Tile>
        <Tile onClick={() => navigate('/admin/refresh-event-sources')}>
          <TileIcon><CalendarClock size={20} /></TileIcon>
          <TileTitle>1. Refresh Event Sources with WebIQ</TileTitle>
          <TileDescription>Find which websites list events for each entity. Creates no events by itself.</TileDescription>
        </Tile>
        <Tile onClick={() => navigate('/admin/extract-events')}>
          <TileIcon><CalendarClock size={20} /></TileIcon>
          <TileTitle>2. Extract Events from Web Sources with AI</TileTitle>
          <TileDescription>Read the websites we already know about and pull the actual events out of them.</TileDescription>
        </Tile>
        <Tile onClick={() => navigate('/admin/cache')}>
          <TileIcon><Boxes size={20} /></TileIcon>
          <TileTitle>Cache Layer</TileTitle>
          <TileDescription>Trigger cache rebuilds, view the build manifest, and inspect the app metadata snapshot.</TileDescription>
        </Tile>
        <Tile onClick={() => navigate('/admin/functions')}>
          <TileIcon><Users size={20} /></TileIcon>
          <TileTitle>Functions Status</TileTitle>
          <TileDescription>Azure Functions performance and scaling (placeholder).</TileDescription>
        </Tile>
      </TileGrid>
    </AdminLayout>
  );
};

export default AdminDashboardPage;
